import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Stack, LinearProgress, Pagination, PaginationItem } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import classNames from 'classnames';
import { scrollXY } from '../../styles/scrollbars';

import ChevronUpIcon from '../../icons/ChevronUpIcon';
import ChevronDownIcon from '../../icons/ChevronDownIcon';

const LoadingOverlay = () => {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        height: '100%',
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
      }}
    >
      <LinearProgress sx={{ width: '300px' }} />
    </Stack>
  );
};

// Clean table surface: hairline row separators, airy row height, a
// quiet hover fill, and small medium-weight gray column headers. Horizontal
// padding is responsive so rows on `flush` surface-card pages line up with the
// card header (40px desktop / 22px mobile) while staying full-bleed to the
// card edges. Shared here so every table (Users, Drivers, Stations, Trips,
// Fleet) gets the same treatment instead of each page restyling its own grid.
const hairlineColor = (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)');
const hoverFill = (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F6F7F8');

// Column-header row height. Exported so consumers that must sit flush beneath
// the sticky header row (e.g. sticky group/section headers) can offset by it
// instead of hardcoding a matching magic number.
export const DATAGRID_HEADER_HEIGHT = 46;

const GridContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '.DataGrid-gutter': {
    padding: '0 20px',
    [theme.breakpoints.up('md')]: { padding: '0 28px' },
  },
  '.DataGrid-main': {
    ...scrollXY,
    display: 'flex',
    flexDirection: 'column',
    '.DataGrid-headers': {
      position: 'sticky',
      top: 0,
      zIndex: 3,
      background: theme.palette.background.paper,
      borderBottom: `1px solid ${hairlineColor(theme)}`,
      '.DataGrid-header': {
        minHeight: `${DATAGRID_HEADER_HEIGHT}px`,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        position: 'relative',
        gap: '8px',
        // A plain rule between columns. This used to be a drag handle (a `|` glyph
        // with `cursor: col-resize`) that resized the column — an affordance nothing
        // asked for, which mostly advertised itself and let a stray drag leave the
        // table a mess. Column widths come from the column config alone now.
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          height: '14px',
          width: '1px',
          backgroundColor: hairlineColor(theme),
        },
        p: {
          width: '100%',
          overflow: 'hidden',
          fontSize: '12.5px',
          fontWeight: 600,
          letterSpacing: '0.01em',
        },
        '.DataGrid-headerIcon': {
          display: 'flex',
          flexShrink: 0,
          color: theme.palette.text.inactive,
          '.MuiSvgIcon-root': { fontSize: 16 },
        },
      },
    },
  },
  '.DataGrid-content': {
    position: 'relative',
    flex: 1,
  },
  '.DataGrid-row': {
    cursor: 'pointer',
    borderBottom: `1px solid ${hairlineColor(theme)}`,
    transition: 'background-color 0.12s ease',
    '&:last-of-type': { borderBottom: 'none' },
    '&:hover, &.Mui-selected, &.Mui-hovered': {
      backgroundColor: hoverFill(theme),
    },
  },
  '.DataGrid-row-group': {
    // Opaque (not a translucent tint) so that when group headers are sticky they
    // cleanly cover one another as you scroll between sections, instead of the
    // previous section's title bleeding through the next one.
    backgroundColor: theme.palette.mode === 'dark' ? '#212124' : '#F2F2F3',
    borderBottom: `1px solid ${hairlineColor(theme)}`,
    '&:hover, &.Mui-selected': {
      backgroundColor: theme.palette.mode === 'dark' ? '#292a2e' : '#EEF0F2',
    },
    '.DataGrid-cell': {
      overflow: 'visible',
    },
  },
  '.DataGrid-cell': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    fontSize: '14px',
    // Keep every cell's text at the compact 14px table size regardless of how a
    // column renders it. Cells that use a custom renderCell with a plain
    // <Typography> would otherwise fall back to MUI's 16px body1 (this is why
    // the Trips rows read larger than Users); normalise them here so all tables
    // stay dense and consistent.
    '.MuiTypography-root': {
      fontSize: '14px',
    },
  },
  '.DataGrid-footer': {
    display: 'flex',
    minHeight: '52px',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}));

const DataGrid = ({
  columns,
  rows,
  onRowClick,
  getRowClassName,
  getRowHeight,
  getRowId,
  hideFooterPagination,
  loading,
  page,
  pageSize,
  paginationMode,
  onPageChange,
  rowCount,
  sortBy,
  sortDir,
  onSortChange,
  ...other
}) => {
  const container = useRef(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);

  const trySelectRow = (index) => {
    const rowItems = document.querySelectorAll('.DataGrid-row');
    rowItems.forEach((item) => item.classList.remove('Mui-hovered'));
    // if there are no rows do nothingjjj
    if (!rowItems.length) {
      return false;
    }
    // get min and max index of rendered rows
    const minIndex = +rowItems[0].getAttribute('data-rowindex');
    const maxIndex = +rowItems[rowItems.length - 1].getAttribute('data-rowindex');

    const _index = Math.max(Math.min(index, maxIndex), minIndex);
    const el = document.querySelector(`.DataGrid-row[data-rowindex="${_index}"]`);
    if (el) {
      el.classList.add('Mui-hovered');
      setSelectedRowIndex(_index);
      return true;
    }
    return false;
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const targetType = event.target.tagName.toUpperCase() || event.srcElement.tagName.toUpperCase();
      if (targetType !== 'INPUT') {
        const key = event.key && event.key.toLowerCase();
        switch (key) {
          case 'j':
            trySelectRow(selectedRowIndex + 1) && event.preventDefault();
            break;
          case 'k':
            trySelectRow(selectedRowIndex - 1) && event.preventDefault();
            break;
          case 'enter':
            rows[selectedRowIndex] && onRowClick && onRowClick({ row: rows[selectedRowIndex] });
            break;
          default:
            return;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [rows, selectedRowIndex, onRowClick]);

  const gridTemplateColumns = columns
    .reduce((style, c) => {
      if (c.minWidth) {
        return [...style, `minmax(${c.minWidth}px,${c.flex || 1}fr)`];
      }
      if (c.maxWidth) {
        return [...style, `minmax(${c.flex || 1}fr,${c.maxWidth}px)`];
      }
      if (c.flex) {
        return [...style, `${c.flex}fr`];
      }
      if (c.width) {
        return [...style, `${c.width}px`];
      }
      return [...style, '1fr'];
    }, [])
    .join(' ');

  // Hoisted once (used by the header row and every data row) so we don't
  // allocate a fresh filtered array per row during render.
  const visibleColumns = columns.filter((c) => !c.hide);

  return (
    <GridContainer
      {...other}
      ref={container}
      className="DataGrid-root"
      role="grid"
      aria-colcount={columns.length}
      aria-rowcount={rows.length}
      aria-multiselectable={false}
    >
      <Box
        className="DataGrid-main"
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={hideFooterPagination ? 0 : 52}
      >
        <Box className="DataGrid-headers" role="rowgroup" width="fit-content" minWidth="100%">
          <Box
            role="row"
            aria-rowindex="1"
            display="grid"
            style={{ gridTemplateColumns }}
            width="fit-content"
            minWidth="100%"
          >
            {visibleColumns.map((c, idx) => {
              // Sorting is opt-in per column (`sortable: true`) and needs an
              // onSortChange handler — every existing table omits both and is
              // untouched. `sortField` overrides the API's sort key when it
              // differs from the column's `field`.
              const sortable = !!c.sortable && !!onSortChange;
              const sortKey = c.sortField || c.field;
              const isSorted = sortable && sortBy === sortKey;
              return (
                <Box
                  key={c.field}
                  className="DataGrid-header DataGrid-gutter"
                  role="columnheader"
                  aria-colindex={idx}
                  aria-sort={isSorted ? (sortDir === 'desc' ? 'descending' : 'ascending') : undefined}
                  display="flex"
                  alignItems="center"
                >
                  {c.headerIcon && <Box className="DataGrid-headerIcon">{c.headerIcon}</Box>}
                  {/* The click target is the label only, never the whole cell —
                      the resize separator sits outside it, so dragging a column
                      edge can't also toggle the sort. */}
                  <Box
                    component={sortable ? 'button' : 'div'}
                    type={sortable ? 'button' : undefined}
                    onClick={sortable ? () => onSortChange(sortKey) : undefined}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                      // flex/minWidth/overflow keep this wrapper the same size the
                      // bare <Typography> used to be, so the container's
                      // `p { width: 100% }` ellipsis still works in every table.
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      border: 'none',
                      padding: 0,
                      background: 'none',
                      font: 'inherit',
                      textAlign: 'left',
                      cursor: sortable ? 'pointer' : 'inherit',
                      color: 'inherit',
                      '&:hover .DataGrid-sortIcon': { opacity: sortable ? 0.45 : 0 },
                    }}
                  >
                    <Typography color={isSorted ? 'text.primary' : 'text.secondary'}>{c.headerName}</Typography>
                    {sortable && (
                      <Box
                        className="DataGrid-sortIcon"
                        component="span"
                        sx={{
                          display: 'flex',
                          // Reserved space, so toggling doesn't shift the label.
                          opacity: isSorted ? 1 : 0,
                          transition: 'opacity 0.12s ease',
                          color: 'text.secondary',
                          '.MuiSvgIcon-root': { fontSize: 15 },
                        }}
                      >
                        {isSorted && sortDir === 'desc' ? <ChevronDownIcon /> : <ChevronUpIcon />}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box className="DataGrid-content" width="fit-content" minWidth="100%">
          {loading && <LoadingOverlay />}
          {!loading &&
            rows.map((row, rowIdx) => {
              const rowHeight = getRowHeight ? getRowHeight({ model: row }) : '60px';
              return (
                <Box
                key={getRowId ? getRowId(row) : row.id}
                className={classNames(
                  'DataGrid-row',
                  rowIdx === selectedRowIndex ? 'Mui-selected' : '',
                  getRowClassName ? getRowClassName({ row }) : '',
                )}
                role="row"
                data-rowid={getRowId ? getRowId(row) : row.id}
                aria-rowindex={2 + rowIdx}
                data-rowindex={rowIdx}
                display="grid"
                style={{ gridTemplateColumns }}
                width="fit-content"
                minWidth="100%"
                position="relative"
                onClick={(e) => {
                  setSelectedRowIndex(rowIdx);
                  onRowClick({ row });
                }}
              >
                {visibleColumns.map((c, colIdx) => (
                    <Box
                      key={c.field}
                      className="DataGrid-cell DataGrid-gutter"
                      role="cell"
                      aria-colindex={colIdx}
                      height={rowHeight}
                      display="flex"
                      alignItems="center"
                      position="relative"
                    >
                      {c.renderCell && c.renderCell({ row })}
                      {!c.renderCell && <Typography sx={{ fontSize: 'inherit', fontWeight: 500 }}>{row[c.field]}</Typography>}
                    </Box>
                  ))}
              </Box>
              );
            })}
        </Box>
      </Box>
      {!hideFooterPagination && (
        <Box className="DataGrid-footer" position="absolute" bottom="0" left="0" right="0">
          <Pagination
            color="spacer"
            page={Math.max(1, page + 1)}
            count={Math.ceil(rowCount / pageSize) || 1}
            renderItem={(p) => <PaginationItem {...p} disableRipple />}
            onChange={(_, v) => onPageChange && onPageChange(v - 1)}
          />
        </Box>
      )}
    </GridContainer>
  );
};

export default DataGrid;
